import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { ITENS_EXEMPLO, TOTAL_EXEMPLO, COLUNAS_TABELA, brl } from './exemploDocumento';

/**
 * Os dois nós próprios do modelo de documento. O JSON que o editor gera
 * (`editor.getJSON()`) é o que a API guarda e o que o gerador de PDF percorre
 * — por isso os nomes `variavel` e `tabelaItens` e o atributo `chave` são
 * contrato, não escolha de front. Ver docs/modelos-documento/especificacao.md §11.
 */

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        variavel: {
            /** Insere o chip da variável no cursor, seguido de um espaço. */
            inserirVariavel: (chave: string) => ReturnType;
        };
        tabelaItens: {
            /** Insere o bloco da tabela de itens abaixo do bloco atual. */
            inserirTabelaItens: () => ReturnType;
        };
    }
}

export interface VariavelOptions {
    /** Nome legível para a chave (vem de GET api/ModeloDocumento/Variaveis). */
    obterNome: (chave: string) => string;
}

/** Nó inline e atômico: o cursor pula por cima, Backspace apaga inteiro. */
export const Variavel = Node.create<VariavelOptions>({
    name: 'variavel',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,

    addOptions() {
        return { obterNome: (chave: string) => chave };
    },

    addAttributes() {
        return {
            chave: {
                default: '',
                parseHTML: (el: HTMLElement) => el.getAttribute('data-variavel') ?? '',
                renderHTML: (attrs: Record<string, unknown>) => ({ 'data-variavel': String(attrs.chave ?? '') }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-variavel]' }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return ['span', mergeAttributes({ class: 'md-var' }, HTMLAttributes), this.options.obterNome(node.attrs.chave)];
    },

    addNodeView() {
        return ({ node }) => {
            const dom = document.createElement('span');
            dom.className = 'md-var';
            dom.setAttribute('data-variavel', node.attrs.chave);
            dom.contentEditable = 'false';
            dom.title = node.attrs.chave;
            dom.textContent = this.options.obterNome(node.attrs.chave);
            return { dom };
        };
    },

    addCommands() {
        return {
            inserirVariavel: (chave: string) => ({ chain }) =>
                chain()
                    .focus()
                    .insertContent([{ type: this.name, attrs: { chave } }, { type: 'text', text: ' ' }])
                    .run(),
        };
    },
});

/** Bloco atômico: no PDF vira a tabela com os itens da oportunidade e o total. */
export const TabelaItens = Node.create({
    name: 'tabelaItens',
    group: 'block',
    atom: true,
    draggable: true,
    selectable: true,

    parseHTML() {
        return [{ tag: 'div[data-tabela-itens]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes({ 'data-tabela-itens': '', class: 'md-tabela' }, HTMLAttributes), 'Tabela de itens'];
    },

    /* Desenhada como sai no PDF, com os itens de exemplo: quem edita vê a
       tabela no lugar dela, não um aviso de que ela vai existir. */
    addNodeView() {
        return () => {
            const dom = document.createElement('div');
            dom.className = 'md-tabela';
            dom.contentEditable = 'false';
            dom.title = 'Tabela de itens: na emissão, sai com os produtos da oportunidade e o total.';

            const el = <K extends keyof HTMLElementTagNameMap>(tag: K, texto = '', classe = '') => {
                const e = document.createElement(tag);
                if (texto) e.textContent = texto;
                if (classe) e.className = classe;
                return e;
            };
            const linha = (celulas: [string, string?][], classe = '', tag: 'th' | 'td' = 'td') => {
                const tr = el('tr', '', classe);
                celulas.forEach(([texto, c]) => tr.append(el(tag, texto, c ?? '')));
                return tr;
            };

            const tabela = el('table', '', 'fdx-tab');
            const cols = el('colgroup');
            COLUNAS_TABELA.forEach(w => { const c = el('col'); if (w) c.style.width = `${w}px`; cols.append(c); });
            const thead = el('thead');
            thead.append(linha([['Código'], ['Descrição'], ['Qtd.', 'n'], ['Unitário', 'n'], ['Total', 'n']], '', 'th'));
            const tbody = el('tbody');
            ITENS_EXEMPLO.forEach(it => tbody.append(linha([
                [it.codigo], [it.descricao], [String(it.qtd), 'n'], [brl(it.unit), 'n'], [brl(it.total), 'n'],
            ])));
            const total = linha([['Total', 'n'], [brl(TOTAL_EXEMPLO), 'n']], 'fdx-tot');
            (total.firstChild as HTMLTableCellElement).colSpan = 4;
            tbody.append(total);

            tabela.append(cols, thead, tbody);
            dom.append(tabela);
            return { dom };
        };
    },

    addCommands() {
        return {
            inserirTabelaItens: () => ({ chain }) =>
                chain().focus().insertContent({ type: this.name }).run(),
        };
    },
});

/** Marca o trecho entre colchetes ("[x] dias"), que só a empresa sabe — o mesmo realce da prévia. */
export const TrechoACompletar = Extension.create({
    name: 'trechoACompletar',

    addProseMirrorPlugins() {
        return [new Plugin({
            props: {
                decorations: state => {
                    const marcas: Decoration[] = [];
                    state.doc.descendants((no, pos) => {
                        if (!no.isText || !no.text) return;
                        for (const m of no.text.matchAll(/\[[^\]]+\]/g)) {
                            const ini = pos + (m.index ?? 0);
                            marcas.push(Decoration.inline(ini, ini + m[0].length, { class: 'fdx-completar' }));
                        }
                    });
                    return DecorationSet.create(state.doc, marcas);
                },
            },
        })];
    },
});
